import type { PortalCreativeRow } from '../../types/portalReport.js';
import { isGroupScope } from './groupScope.js';

// CLIENT PORTAL RESTYLE — merging the creative cards.
//
// ⚠️⚠️ PRESENTATION ONLY, AND ONLY INSIDE THE CREATIVE GRID. The campaign table,
// the hero figures, the totals, the charts and the demographics all keep working
// on UNGROUPED ad rows. NOTHING that feeds a headline number goes through this
// file. Scott was explicit: "this rule is ONLY for the media image section."
//
// THE PROBLEM IT SOLVES. One creative is routinely trafficked as several ads
// across near-identical campaigns, so the grid shows the same "Video -
// Walkaround" three times at three different costs per lead. A client reads that
// as three ads and cannot tell which figure to believe.
//
// THE RULE. Two rows merge when BOTH hold:
//   - the AD NAME is identical (exact, after trimming and collapsing whitespace),
//   - the CAMPAIGN NAMES are identical after stripping at most ONE TRAILING
//     VERSION TOKEN.
//
// ⚠️⚠️ THIS REPLACED AN 85%-SIMILARITY RULE THAT SHIPPED, BEHAVED EXACTLY AS
// SPECIFIED, AND WAS STILL WRONG. Measured on Snap Fitness Mount Druitt's real
// August campaign family with BigQuery's own EDIT_DISTANCE. Two findings, and the
// first is fatal to the APPROACH rather than to the number:
//
//   1. THE RELATION WAS NOT TRANSITIVE, SO "THE GROUP" WAS UNDEFINED.
//      A = "… $6pw for 6 Weeks - July 26"
//      B = "… $6pw for 6 Weeks - WL - July 26"
//      C = "… $6pw for 6 Weeks - WL - July 26 – V2"
//      A~B 89.1 (merge) · B~C 90.2 (merge) · A~C 80.4 (APART). Whatever the
//      implementation did it was picking one pairing arbitrarily, and which cards
//      ended up merged depended on iteration order. That is a property of
//      threshold clustering: NO value of the threshold fixes it, moving 85 only
//      moves which triple is intransitive. It is why the grid looked half-merged.
//
//   2. THE SUFFIXES ARE AUDIENCES, AND CHARACTER SIMILARITY CANNOT TELL THEM
//      FROM A VERSION. `WL`, `ABOT`, `LG` are targeting variants; `– V2` is a
//      version of the same campaign. They cost about the same number of edits, so
//      at 85% the rule merged "$6pw for 6 Weeks" with "$6pw for 6 Weeks - WL"
//      (89.1) and "WL" with "ABOT" (85.1) — two different audiences, whose cost
//      per lead legitimately differs, blended into one figure — while REFUSING
//      "July 26" against "July 26 – V2" (80.4), which is the case Scott actually
//      asked for. It merged what should stay apart and separated what should
//      join. Not a tuning problem: a metric that cannot represent the distinction.
//
// ⚠️ EQUALITY IS TRANSITIVE, so groups are well-defined and order-independent BY
// CONSTRUCTION rather than by luck. That is the whole reason for the change, and
// `creativeRowsMerge` + the property test over the six real names lock it down.
//
// ⚠️⚠️ DO NOT ADD A SIMILARITY FALLBACK for names the pattern does not match. An
// unrecognised suffix (`copy`, `FINAL`, `- 2`, `Test`) leaves the names different,
// so the rows stay as TWO CARDS — the safe direction, and it must stay that way.
// Extra cards are cosmetic; a blended cost per lead across two audiences is a
// number the client quotes back at you. A fallback reintroduces every problem
// above, including the intransitivity.

/** Lowercased, whitespace-collapsed, trimmed. The form names are compared in. */
export function normalizeCampaignName(name: string | null): string {
  return (name ?? '').toLowerCase().replace(/\s+/g, ' ').trim();
}

/**
 * ⚠️⚠️ AT MOST ONE TRAILING VERSION TOKEN, AND NOTHING ELSE, EVER.
 *
 * Recognised forms, on an already-normalised (lowercased, whitespace-collapsed)
 * name — trailing only:
 *
 *   "… july 26 – v2"   separator + v + digits   (en dash, hyphen or em dash)
 *   "… july 26 v2"     bare, space-separated
 *   "… july 26 (2)"    parenthesised integer, with or without the v
 *
 * ⚠️⚠️ `WL`, `ABOT`, `LG` AND ANYTHING LIKE THEM ARE NEVER STRIPPED. They are
 * AUDIENCE SEGMENTS, and stripping one merges two audiences into a single cost
 * per lead — which is the exact error the old 85%-similarity rule was making.
 * The pattern cannot reach them: every branch requires either `v` followed by
 * digits or a parenthesised integer, and none of those is a bare word. That is
 * structural rather than remembered, and it is asserted directly rather than as
 * an implication of a merge test.
 *
 * ⚠️⚠️ THE DASH CHARACTERS MATTER, AND THIS IS THE FAILURE THAT LOOKS LIKE THE
 * FEATURE NOT BEING WIRED UP. Mount Druitt's real campaign names use an EN DASH
 * (`–`, U+2013) before `V2`, not a hyphen. A pattern written with `-` alone
 * matches NOTHING on the real data while passing every hand-written fixture, so
 * all three dashes are handled and an en-dash case is pinned by its own test.
 *
 * ⚠️ A BARE SEPARATOR PLUS DIGITS (`- 2`) IS NOT A VERSION TOKEN. It is one of
 * the unrecognised suffixes the brief names, so it leaves the names different
 * and the rows stay as two cards. Digits alone qualify only inside parentheses,
 * where the intent is unambiguous.
 *
 * ⚠️ NOTHING IN THE MIDDLE OF A NAME IS EVER TOUCHED.
 */
export function stripTrailingVersionToken(normalized: string): string {
  return normalized.replace(/(?:\s*[-–—]?\s*\(\s*v?\d+\s*\)|\s*[-–—]\s*v\d+|\s+v\d+)$/, '').trim();
}

/**
 * The campaign half of the merge key: normalised, with a trailing version token
 * removed. Two rows merge only when these are EQUAL — see the header for why
 * equality replaced similarity.
 */
export function campaignMergeKey(name: string | null): string {
  return stripTrailingVersionToken(normalizeCampaignName(name));
}

/** One card on the grid: either a single ad, or several merged into one. */
export interface GroupedCreativeRow {
  /** Stable React key — the representative ad's id. */
  key: string;
  /**
   * The club every merged row on this card came from.
   *
   * ⚠️⚠️ TRUE BY CONSTRUCTION, not by care: `accountId` is a MERGE KEY SEGMENT,
   * so rows from two clubs cannot land on one card. That is what makes it safe
   * to label — and before §7 it was not true, which is why the label could not
   * simply have been added.
   */
  accountId: string;
  adName: string | null;
  /** The representative campaign's name; `campaignCount` says how many there are. */
  campaignName: string | null;
  currency: string;
  spend: number;
  leads: number;
  /** ⚠️ RECOMPUTED from the combined figures — never an average. See below. */
  costPerLead: number | null;
  /**
   * Summed like spend and leads. ⚠️ NULL when ANY merged row lacks the field —
   * a partial sum printed as the card's total would understate it silently.
   */
  purchases: number | null;
  landingPageViews: number | null;
  thumbnailUrl: string | null;
  status: string | null;
  /** How many ad rows this card represents. 1 = nothing was merged. */
  mergedAdCount: number;
  /** How many DISTINCT campaign names those rows came from. */
  campaignCount: number;
}

/**
 * ⚠️⚠️ COST PER LEAD IS RECOMPUTED AS COMBINED SPEND ÷ COMBINED LEADS. NEVER the
 * average of the merged rows' own cost-per-lead figures — the do-not-average rule
 * this whole warehouse follows. Three "Video - Walkaround" cards at $13.94 /
 * $10.12 / … become one card whose figure is neither their average nor any of
 * them, and that figure is the true one.
 *
 * ⚠️ Zero leads is NULL, never $0.00 and never spend-as-a-cost. Money spent for
 * nothing is not a cost per lead of zero, and zero is the most flattering
 * possible misstatement.
 */
function recomputeCostPerLead(spend: number, leads: number): number | null {
  if (leads <= 0) return null;
  return spend / leads;
}

/**
 * ⚠️ ACTIVE IF ANY MERGED INSTANCE IS ACTIVE, otherwise the shared status when
 * they agree, otherwise nothing. Scott's call, and it matches what a client is
 * actually asking — "is this still running".
 *
 * ⚠️ THE EXISTING REFUSAL TO DEFAULT TO "PAUSED" IS KEPT, and extended: an
 * UNKNOWN status is not agreement. One paused instance beside one we know
 * nothing about does not make the card paused — the unknown one may be running,
 * and a wrong "Paused" tells a client an ad is off when it is not. No pill is
 * the honest answer there, exactly as it is for a single row with no status.
 */
export function resolveMergedStatus(statuses: Array<string | null>): string | null {
  const active = statuses.find((s) => s !== null && s.trim().toLowerCase() === 'active');
  if (active !== undefined) return active;
  if (statuses.some((s) => s === null || s.trim() === '')) return null;
  const first = statuses[0] ?? null;
  return statuses.every((s) => s === first) ? first : null;
}

/**
 * ⚠️ THE THUMBNAIL COMES FROM THE FRESHEST `refreshedAt` THAT HAS ONE. Meta CDN
 * URLs are short-lived, so an older row is likelier to 404 into the "No preview"
 * placeholder.
 *
 * ⚠️ "That HAS one" is the load-bearing half: picking the freshest row outright
 * would hand back a null whenever the newest row happens to carry no image,
 * guaranteeing the placeholder this rule exists to avoid. A row with no
 * `refreshedAt` sorts last rather than being dropped — an unknown age still
 * beats no picture.
 */
function pickThumbnail(rows: PortalCreativeRow[]): string | null {
  let best: PortalCreativeRow | null = null;
  for (const r of rows) {
    if (!r.thumbnailUrl) continue;
    if (best === null) {
      best = r;
      continue;
    }
    if ((r.refreshedAt ?? '') > (best.refreshedAt ?? '')) best = r;
  }
  return best?.thumbnailUrl ?? null;
}

/** Trimmed and whitespace-collapsed, but NOT lowercased — "identical" means identical. */
function normalizeAdName(name: string | null): string {
  return (name ?? '').replace(/\s+/g, ' ').trim();
}

/**
 * A separator no name can contain, so no combination of field values can collide
 * across fields.
 */
const KEY_SEP = '\u0000';

/**
 * ⚠️⚠️ THE KEY'S SEGMENTS, AND THE IDENTITY TYPE IS DERIVED FROM THEM.
 *
 * The inversion is the point: a card's identity is DEFINED as this list, so a
 * field cannot exist on the identity without being in the key. The alternative
 * — an identity interface plus a hand-written join — is two lists that agree by
 * care, and §7 exists because exactly that kind of agreement lapsed.
 *
 * ⚠️⚠️ `accountId` IS FIRST, AND ITS ABSENCE WAS THE DEFECT. Franchise clubs run
 * the SAME TEMPLATED ad and campaign names, so on a group report the same-named
 * ad at six clubs merged into ONE card and its spend and leads were summed
 * across them — a figure attributed to nobody, on the surface Scott then asked
 * us to label with a club name. A single club label on such a card would have
 * been a figure labelled with the wrong source.
 */
const KEY_SEGMENTS = ['accountId', 'adName', 'currency', 'campaign'] as const;

/**
 * What makes two rows the same card. Every field is REQUIRED, so the object
 * literal in `creativeMergeKey` fails `tsc` if a segment is left out.
 */
type CreativeIdentity = Record<(typeof KEY_SEGMENTS)[number], string>;

/**
 * ⚠️⚠️ THE MERGE KEY. Rows merge exactly when their keys are EQUAL, which is
 * what makes grouping transitive, well-defined and independent of the order the
 * rows arrive in. `null` means "this row can never merge with anything".
 *
 * ⚠️⚠️ TWO ROWS MERGE ONLY WHEN THEY COME FROM THE SAME CLUB. Added §7
 * (2026-09-04) so a card is one club BY CONSTRUCTION and the label on it cannot
 * be wrong. Consequence, and it is the honest one: the card count RISES on a
 * group report — one per club per creative — because each card's spend and leads
 * now belong to the club named on it.
 *
 * ⚠️ NO EFFECT ON A SINGLE-CLUB REPORT: one account means one value in that
 * segment, so the partition is unchanged. Asserted, not assumed.
 *
 * ⚠️ TWO ROWS MERGE ONLY WHEN THEY SHARE A CURRENCY. Not in either brief, and
 * not optional: a group login can span an AU and an NZ club, and summing their
 * spend would produce a figure that exists in no currency — the one thing this
 * report never does. In practice one ad has one currency, so it costs nothing
 * and closes a case that would otherwise be silent if it ever happened.
 *
 * ⚠️⚠️ AND THE ACCOUNT IS THE SAME ARGUMENT WITH THE OPPOSITE LIKELIHOOD. For a
 * franchise group, cross-club identical ad names are not a rare edge — they are
 * the normal case. The reasoning that added currency argued more strongly for
 * adding the account, and had done since the day it was written.
 *
 * ⚠️ AN AD WITH NO NAME NEVER MERGES. "Identical ad name" needs a name; an
 * absent one is not evidence that two rows are the same creative.
 *
 * ⚠️ NOR DOES A ROW WITH NO CAMPAIGN NAME. Under the old similarity rule two
 * empty names scored 0 and so could not merge; under plain equality two empty
 * strings are equal, which would merge every unnamed campaign into one card.
 * Keeping the refusal preserves that behaviour and is the safe direction.
 */
export function creativeMergeKey(row: PortalCreativeRow): string | null {
  return creativeIdentityKey(row);
}

/**
 * ⚠️ THE KEY FROM ITS FOUR FIELDS ALONE, so a surface whose rows are not
 * `PortalCreativeRow`s (the automotive Meta panels carry their own counts) merges on
 * EXACTLY the same rule rather than a copy of it. `creativeMergeKey` is this.
 */
export function creativeIdentityKey(row: {
  accountId: string;
  adName: string | null;
  campaignName: string | null;
  currency: string;
}): string | null {
  const adName = normalizeAdName(row.adName);
  if (adName === '') return null;
  const campaign = campaignMergeKey(row.campaignName);
  if (campaign === '') return null;
  const identity: CreativeIdentity = {
    accountId: row.accountId,
    adName,
    currency: row.currency,
    campaign,
  };
  return KEY_SEGMENTS.map((f) => identity[f]).join(KEY_SEP);
}

/**
 * Whether two rows belong on the same card.
 *
 * ⚠️ Derived from `creativeMergeKey` rather than reimplemented, so the pairwise
 * predicate the transitivity test asserts over cannot drift from what
 * `groupCreativeRows` actually does. Key equality is an equivalence relation, so
 * transitivity is a property of the definition rather than something the grouping
 * has to be careful about — which is precisely what the 85% rule could not give.
 */
export function creativeRowsMerge(a: PortalCreativeRow, b: PortalCreativeRow): boolean {
  const ka = creativeMergeKey(a);
  return ka !== null && ka === creativeMergeKey(b);
}

/**
 * Merge the grid's rows into cards.
 *
 * ⚠️⚠️ GROUPED BY KEY, NOT BY PAIRWISE COMPARISON — and that is the point of the
 * whole change. The old rule compared each row against a group's first member,
 * because similarity is not transitive and comparing against ANY member would
 * chain A~B~C into one card even where A and C were 20 points apart. That choice
 * made membership depend on which row happened to arrive first, so the answer was
 * arbitrary in exactly the cases that mattered. Key equality removes the question:
 * a row belongs to the group with its key, order is irrelevant, and there is no
 * representative to pick.
 *
 * ⚠️ Output order follows FIRST APPEARANCE of each group, so the query's
 * leads-DESC, spend-DESC ordering still drives the grid.
 */
export function groupCreativeRows(rows: PortalCreativeRow[]): GroupedCreativeRow[] {
  const groups: PortalCreativeRow[][] = [];
  const byKey = new Map<string, PortalCreativeRow[]>();
  for (const row of rows) {
    const key = creativeMergeKey(row);
    // ⚠️ A row that can never merge gets its OWN group rather than a shared
    // bucket — otherwise every unnamed ad would collapse into a single card.
    if (key === null) {
      groups.push([row]);
      continue;
    }
    const existing = byKey.get(key);
    if (existing) {
      existing.push(row);
      continue;
    }
    const group = [row];
    byKey.set(key, group);
    groups.push(group);
  }

  return groups.map((members) => {
    const head = members[0] as PortalCreativeRow;
    // ⚠️ Spend, leads, purchases and landing page views are the additive fields
    // on the row. Everything else is either an identity or a ratio.
    const spend = members.reduce((s, m) => s + m.spend, 0);
    const leads = members.reduce((s, m) => s + m.leads, 0);
    const sumOptional = (pick: (m: PortalCreativeRow) => number | undefined): number | null =>
      members.every((m) => pick(m) !== undefined)
        ? members.reduce((s, m) => s + (pick(m) as number), 0)
        : null;
    const campaignNames = new Set(
      members.map((m) => normalizeCampaignName(m.campaignName)).filter((n) => n !== ''),
    );
    return {
      key: head.adId,
      // ⚠️ The head's club IS the card's club — see the field's own note.
      accountId: head.accountId,
      adName: head.adName,
      campaignName: head.campaignName,
      currency: head.currency,
      spend,
      leads,
      costPerLead: recomputeCostPerLead(spend, leads),
      purchases: sumOptional((m) => m.purchases),
      landingPageViews: sumOptional((m) => m.landingPageViews),
      thumbnailUrl: pickThumbnail(members),
      status: resolveMergedStatus(members.map((m) => m.status)),
      mergedAdCount: members.length,
      campaignCount: campaignNames.size,
    };
  });
}

/**
 * ⚠️⚠️ THE CARD STATES WHAT IT MERGES. Without it a client cannot reconcile the
 * creative grid against the campaign table — three ads' spend appearing under one
 * heading — and will assume one of the two is wrong.
 *
 * Returns null when nothing was merged: a label on every card would be noise,
 * and "1 ad across 1 campaign" says nothing.
 */
export function mergedCreativeLabel(row: GroupedCreativeRow): string | null {
  if (row.mergedAdCount <= 1) return null;
  const ads = `${row.mergedAdCount} ads`;
  // ⚠️ A merged card whose campaign names were all blank has a count of 0. It
  // still merged something, so it still says so — it just cannot name campaigns
  // it does not have.
  if (row.campaignCount <= 1) return `${ads} combined`;
  return `${ads} across ${row.campaignCount} campaigns`;
}

/**
 * Which club to print on a creative card, or null for none.
 *
 * ⚠️⚠️ ONLY ON A MULTI-CLUB SCOPE. On a single-club report the same club name on
 * every card is noise — the same judgement as the location picker hiding below
 * two locations, and the nav omitting an "All locations" row above a list of
 * one. A drill-down to one club therefore drops the labels, which is right: the
 * report's own heading already names it.
 *
 * ⚠️⚠️ RULE 12 — NEVER INFER. An `accountId` that does not resolve to a name in
 * the payload's `accounts` renders NOTHING. Not the id dressed as a name, and
 * not a placeholder: a client reading "Unknown club" beside real spend learns
 * less than one reading no label at all, and an id printed as a name is a
 * fabricated fact about whose money that was.
 *
 * ⚠️ A blank stored name is treated as unresolved for the same reason — an empty
 * label is a gap wearing the shape of an answer.
 */
export function creativeClubLabel(
  accounts: ReadonlyArray<{ id: string; name: string }>,
  accountId: string,
): string | null {
  // ⚠️ THE SHARED GATE, not a fourth copy of `length <= 1` — see groupScope.ts.
  if (!isGroupScope(accounts)) return null;
  const name = accounts.find((a) => a.id === accountId)?.name?.trim();
  return name === undefined || name === '' ? null : name;
}
